import { FC, useMemo } from 'react';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Skeleton from 'components/commercetools-ui/atoms/skeleton';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import { useCart } from 'frontastic';
import AmazonPayExpressButton from '../../checkout/components/steps/sections/payment/components/amazon-pay-express';
import ApplePayExpressButton from '../../checkout/components/steps/sections/payment/components/apple-pay-express';
import { CheckoutButtonProps } from '../types';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';

const CheckoutButton: FC<CheckoutButtonProps> = ({ className, link, disabled, onClick, isLoading }) => {
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });

  const { data } = useCart();
  const { getCurrency } = useLocale();
  const currencySelected = getCurrency();

  const hasGiftCardItems = useMemo(() => {
    return data?.lineItems?.some((item) => item?.isGiftcard);
  }, [data?.lineItems]);

  const isAmazonPayAvailable = useMemo(() => {
    return currencySelected == 'GBP';
  }, [currencySelected]);

  const isApplePayAvailable = useMemo(() => {
    const hasApplePayAPI = typeof window.ApplePaySession !== 'undefined';
    const isAppleDevice = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
    return hasApplePayAPI && isAppleDevice;
  }, []);

  const isExpressCheckoutAvailable = isApplePayAvailable || isAmazonPayAvailable;

  return (
    <div className={className}>
      {!hasGiftCardItems && isExpressCheckoutAvailable && (
        <>
          <div className="relative mb-14 border border-neutral-400 p-14">
            <div className="text-16 font-bold uppercase text-secondary-black">
              {formatCartMessage({ id: 'express.checkout', defaultMessage: 'Express Checkout' })}
            </div>
            <div className={`mt-14 ${disabled ? 'pointer-events-none h-0 cursor-default opacity-0' : ''}`}>
              {isApplePayAvailable && <ApplePayExpressButton isLoading={isLoading} />}
              {isAmazonPayAvailable && <AmazonPayExpressButton />}
            </div>

            {isApplePayAvailable && (
              <div className="mb-12">
                <div className={`relative mb-8 h-50 ${disabled ? 'block' : 'hidden'}`}>
                  <Skeleton></Skeleton>
                </div>
              </div>
            )}

            {isAmazonPayAvailable && (
              <div className="mb-8">
                <div className={`relative mb-8 h-50 ${disabled ? 'block' : 'hidden'}`}>
                  <Skeleton></Skeleton>
                </div>
                <div className={`relative mx-18 h-18 ${disabled ? 'block' : 'hidden'}`}>
                  <Skeleton></Skeleton>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <div className="border-t border-neutral-400 pt-14">
        <Link link={link}>
          <Button
            variant="primary"
            iconPosition="right"
            size="l"
            onClick={onClick}
            disabled={disabled}
            className="flex w-full justify-center font-black"
          >
            <span className="text-large relative top-0 font-body text-[16px] font-bold">
              {formatCartMessage({ id: 'cart.goto.checkout', defaultMessage: 'Go to checkout' })}
              <i className="icon icon-arrow_right relative top-0 ml-6 text-sm "></i>
            </span>
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default CheckoutButton;
